import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';

interface ManualChipProps {
  /** Ручной ввод открыт — пилюля нажата, повторное нажатие закрывает его. */
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

/** Карандаш — значок ручной карточки (и в поле строки, пока открыт ручной ввод). */
export function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M15.5 5.5l3 3L8 19H5v-3z" />
      <path d="M13.5 7.5l3 3" />
    </svg>
  );
}

/**
 * «Вручную» во втором ряду строки, рядом с чипом модуля (решение владельца 2026-10-21): сразу открывает ряд
 * перевода — слово и перевод вводятся без словаря. Пилюля той же высоты, что чип модуля, в области нажатия 44 px.
 * Это переключатель, и состояние видно явно (владелец 2026-10-22: «ощущение, что он всегда включен»): выключен —
 * контур без заливки и серый мини-переключатель, включён — оранжевая заливка (без рамки) и переключатель вправо.
 */
export function ManualChip({ active, disabled = false, onToggle }: ManualChipProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      // Касание не уводит фокус из поля: клавиатура телефона остаётся.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={active}
      className="group flex h-11 flex-none items-center disabled:opacity-60"
    >
      <span
        className={cn(
          'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-pill border border-solid pl-2.5 pr-3 text-[13px] font-medium transition-colors',
          // Включён — заливка и переключатель, без оранжевой рамки (владелец 2026-10-22: выделять не бордером).
          active
            ? 'border-transparent bg-accent/15 text-fg'
            : 'border-line bg-transparent text-muted group-hover:border-[#3a3a40] group-hover:text-fg',
        )}
      >
        {/* До 390 px карандаш скрыт: в ряду с чипом модуля и стрелкой место есть только под подпись и переключатель (на
            360 px имя модуля ужималось до «До…», проверка 28.10). */}
        <PencilIcon className={cn('h-4 w-4 flex-none max-[389px]:hidden', active ? 'text-accent' : 'text-muted')} />
        {t('homeComposer.manualButton')}
        <span
          aria-hidden
          className={cn('relative ml-0.5 h-3.5 w-6 flex-none rounded-full transition-colors', active ? 'bg-accent' : 'bg-white/[0.14]')}
        >
          <span
            className={cn(
              'absolute top-[2px] h-2.5 w-2.5 rounded-full transition-[left,background-color] duration-150',
              active ? 'left-[12px] bg-fg' : 'left-[2px] bg-[#8e8e93]',
            )}
          />
        </span>
      </span>
    </button>
  );
}
